import { useApi, useLoad } from "@allergy123/api-client/react";
import { PREVALENCE_GRADES, SEASONS, SEVERITY_GRADES, type EntryInput, type RuleIssue } from "@allergy123/contracts/allergens";
import { Button, Card, Field, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { CatalogHeader, errorText, publishIssues } from "./common";

interface Row {
  antigenId: string;
  displayName: string;
  abbreviation: string;
}

/** The same look as the Select component, for the compact in-table grade pickers that carry an aria-label instead of a visible one. */
const CELL_SELECT =
  "min-h-10 rounded-sm border border-rule-strong bg-card px-3 py-2 text-body text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:bg-disabled disabled:text-on-disabled";
const GRADE_NONE = [{ value: "", label: "—" }];

const canonicalSeasons = (seasons: readonly string[]) => SEASONS.filter((s) => seasons.includes(s)).join(",");
/** Same grades and the same seasons, whatever order the seasons were listed in. */
const sameEntry = (a: EntryInput, b: EntryInput) => a.prevalence === b.prevalence && a.severity === b.severity && canonicalSeasons(a.seasons) === canonicalSeasons(b.seasons);

/** Keyed by id, so moving between versions (for example to a new draft) never shows the last one's edits, check results or messages. */
export function ProfileVersionPage() {
  const { id = "" } = useParams();
  return <ProfileVersionEditor key={id} id={id} />;
}

function ProfileVersionEditor({ id }: { id: string }) {
  const api = useApi();
  const navigate = useNavigate();
  const data = useLoad((a) => a.admin.catalog.profileVersion(id));
  const families = useLoad((a) => a.admin.catalog.profiles());
  const antigens = useLoad((a) => a.admin.catalog.antigens());
  // Only what has been changed and not yet saved; everything else is read straight from the loaded version.
  const [edits, setEdits] = useState<Map<string, EntryInput>>(new Map());
  // Antigens added to this draft in the page and not saved yet (they are not in the loaded version).
  const [added, setAdded] = useState<Row[]>([]);
  const [addId, setAddId] = useState("");
  const [issues, setIssues] = useState<RuleIssue[] | null>(null);
  const [note, setNote] = useState("");
  const [compareTo, setCompareTo] = useState("");
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const v = data.data;
  const isDraft = v?.status === "draft";

  const saved = useMemo(() => new Map((v?.entries ?? []).map((e) => [e.antigenId, { antigenId: e.antigenId, prevalence: e.prevalence, severity: e.severity, seasons: e.seasons } satisfies EntryInput])), [v]);
  // An added antigen stops being "added" the moment the loaded version has it, so it is never listed twice while the page catches up.
  const visibleAdded = useMemo(() => added.filter((a) => !saved.has(a.antigenId)), [added, saved]);
  const list: Row[] = useMemo(() => [...(v?.entries ?? []), ...visibleAdded], [v, visibleAdded]);
  const current = (antigenId: string): EntryInput | undefined => edits.get(antigenId) ?? saved.get(antigenId);
  // An edit changed back to what is saved is not a change: it isn't counted and isn't sent.
  const changes = useMemo(() => [...edits.values()].filter((e) => { const was = saved.get(e.antigenId); return !was || !sameEntry(e, was); }), [edits, saved]);
  const changedIds = useMemo(() => new Set(changes.map((e) => e.antigenId)), [changes]);
  const pending = changes.length;

  // When a (re)loaded version arrives, edits it already contains are done with. Anything else the person changed
  // meanwhile, for example while the reload was in flight, is kept.
  useEffect(() => {
    setEdits((m) => {
      const next = new Map([...m].filter(([antigenId, e]) => { const was = saved.get(antigenId); return !was || !sameEntry(e, was); }));
      return next.size === m.size ? m : next;
    });
    setAdded((rows) => {
      const next = rows.filter((a) => !saved.has(a.antigenId));
      return next.length === rows.length ? rows : next;
    });
  }, [saved]);

  const change = (antigenId: string, patch: Partial<EntryInput>) =>
    setEdits((m) => {
      const base = m.get(antigenId) ?? saved.get(antigenId);
      return base ? new Map(m).set(antigenId, { ...base, ...patch }) : m;
    });
  const addAntigen = (antigenId: string) => {
    const a = antigens.data?.antigens.find((x) => x.id === antigenId);
    if (!a || list.some((r) => r.antigenId === antigenId)) return;
    setAdded((rows) => [...rows, { antigenId: a.id, displayName: a.displayName, abbreviation: a.abbreviation }]);
    setEdits((m) => new Map(m).set(a.id, { antigenId: a.id, prevalence: null, severity: null, seasons: [] }));
    setAddId("");
  };

  /** An added antigen that has not been saved has no row on the server, so it can simply be taken back. */
  const removeAdded = (antigenId: string) => {
    setAdded((rows) => rows.filter((a) => a.antigenId !== antigenId));
    setEdits((m) => {
      const next = new Map(m);
      next.delete(antigenId);
      return next;
    });
  };

  async function save() {
    if (busy || pending === 0) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.admin.catalog.saveProfileEntries(id, changes);
      setMsg({ kind: "info", text: `Saved ${pending} entr${pending === 1 ? "y" : "ies"}.${issues ? " Check again to see what is left." : ""}` });
      setIssues(null);
      data.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  async function check() {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.admin.catalog.checkProfile(id);
      setIssues(res.issues);
      setMsg(res.issues.length ? { kind: "error", text: `${res.issues.length} antigen(s) on the paired panels have no entry.` } : { kind: "info", text: "Ready to publish." });
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  async function publish() {
    if (busy || !v) return;
    if (!window.confirm(`Publish ${v.family.code} version ${v.version}? The testing algorithm reads it for every location on this profile.`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.admin.catalog.publishProfile(id, note.trim() || undefined);
      setMsg({ kind: "info", text: `Published ${v.family.code} version ${v.version}.` });
      setIssues(null);
      data.reload();
    } catch (err) {
      // A refusal for broken rules lists the missing entries, as the check does.
      const refused = publishIssues(err);
      if (refused) setIssues(refused);
      setMsg({ kind: "error", text: refused ? `${refused.length} antigen(s) on the paired panels have no entry.` : errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  async function newDraft() {
    if (busy || !v) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.admin.catalog.createProfileDraft(v.family.id, v.id);
      navigate(`/catalog/profiles/versions/${res.draft.id}`);
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  // Every published and draft version of every profile except this one, so any two versions or profiles can be compared.
  const compareChoices = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    for (const f of families.data?.families ?? []) {
      if (f.published && f.published.id !== id) out.push({ value: f.published.id, label: `${f.code} v${f.published.version} (published)` });
      if (f.draft && f.draft.id !== id) out.push({ value: f.draft.id, label: `${f.code} v${f.draft.version} (draft)` });
    }
    return out;
  }, [families.data, id]);
  // Controls are never part of a profile; an antigen already listed can't be added twice.
  const addable = useMemo(() => {
    const listed = new Set(list.map((r) => r.antigenId));
    return (antigens.data?.antigens ?? []).filter((a) => !a.controlKind && !listed.has(a.id)).map((a) => ({ value: a.id, label: `${a.displayName} (${a.abbreviation})` }));
  }, [antigens.data, list]);

  return (
    <>
      <CatalogHeader title={v ? `${v.family.name} · v${v.version} (${v.status})` : "Allergen profile"} intro={!v ? "One version of an allergen profile and its antigens." : isDraft ? "Change grades and seasons, save, check, then publish." : "Published and retired versions are read-only."} />
      {data.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load the profile (${errorText(data.error)})`} /></div>}
      {families.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load the profile list for comparing (${errorText(families.error)})`} /></div>}
      {antigens.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load antigens (${errorText(antigens.error)})`} /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      {issues && issues.length > 0 && (
        <Card as="section" dense className="mb-6" aria-label="Issues to fix">
          <h2 className="mb-2 text-body font-semibold text-anchor">Issues to fix ({issues.length})</h2>
          <ul className="grid gap-1 text-small">
            {issues.map((i, n) => (
              <li key={n}>
                {i.message}
                {isDraft && i.antigenId && !list.some((r) => r.antigenId === i.antigenId) && (
                  <>
                    {" "}
                    <button type="button" onClick={() => addAntigen(i.antigenId!)} disabled={!antigens.data} className="font-medium text-anchor underline">Add an entry</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {v && (
        <div className="mb-6 flex flex-wrap items-end gap-3">
          {isDraft ? (
            <>
              <Button disabled={busy || pending === 0} onClick={() => void save()}>{pending ? `Save ${pending} change${pending === 1 ? "" : "s"}` : "Save changes"}</Button>
              <Button variant="secondary" disabled={busy || pending > 0} onClick={() => void check()}>Check</Button>
              <Field label="Publish note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="What changed and why" />
              <Button disabled={busy || pending > 0} onClick={() => void publish()}>Publish</Button>
            </>
          ) : (
            v.status === "published" && <Button variant="secondary" disabled={busy} onClick={() => void newDraft()}>Start a new draft from this version</Button>
          )}
          {v.basedOnVersionId && <Link to={`/catalog/profiles/compare?a=${v.basedOnVersionId}&b=${v.id}`} className="self-end pb-2 text-anchor underline">Compare with the version it started from</Link>}
          {isDraft && pending > 0 && <p className="w-full text-small text-ink-secondary">Save your changes before checking or publishing: only saved entries are checked and published.</p>}
        </div>
      )}
      {v && (
        <div className="mb-6">
          <div className="flex flex-wrap items-end gap-3">
            <Select
              label="Compare with…"
              value={compareTo}
              onChange={(e) => setCompareTo(e.target.value)}
              options={[{ value: "", label: families.state === "loading" ? "Loading…" : compareChoices.length ? "Choose a version" : "No other versions yet" }, ...compareChoices]}
            />
            <Button variant="secondary" className="mr-5" disabled={!compareTo} onClick={() => navigate(`/catalog/profiles/compare?a=${id}&b=${compareTo}`)}>Compare</Button>
            {isDraft && (
              <>
                <Select
                  label="Add an antigen"
                  value={addId}
                  onChange={(e) => setAddId(e.target.value)}
                  options={[{ value: "", label: antigens.state === "loading" ? "Loading…" : addable.length ? "Choose an antigen" : "Every antigen is already listed" }, ...addable]}
                />
                <Button variant="secondary" disabled={!addId} onClick={() => addAntigen(addId)}>Add to this profile</Button>
              </>
            )}
          </div>
          {isDraft && <p className="mt-2 max-w-[68ch] text-small text-ink-secondary">To add an antigen this profile does not list yet, for example one just added to a tray panel, choose it, add it, set its grades, then save.</p>}
        </div>
      )}
      {data.state === "loading" && <p className="text-ink-secondary">Loading…</p>}
      {v && (
        <Card>
          <Table caption={`${list.length} antigens`}>
            <thead>
              <tr>
                <Th>Antigen</Th>
                <Th>Prevalence</Th>
                <Th>Severity</Th>
                <Th>Seasons</Th>
              </tr>
            </thead>
            <tbody>
              {list.map((e) => {
                const row = current(e.antigenId);
                if (!row) return null;
                const unsaved = changedIds.has(e.antigenId);
                const removable = visibleAdded.some((a) => a.antigenId === e.antigenId);
                return (
                  <tr key={e.antigenId}>
                    <Td>
                      {e.displayName} <span className="text-ink-secondary">({e.abbreviation})</span>
                      {unsaved && <div className="text-small text-ink-secondary">{removable ? "Added, not saved yet" : "Unsaved change"}</div>}
                      {removable && (
                        <div className="mt-2">
                          <Button variant="secondary" surface="card" disabled={busy} aria-label={`Remove ${e.displayName}`} onClick={() => removeAdded(e.antigenId)}>Remove</Button>
                        </div>
                      )}
                    </Td>
                    <Td>
                      <select className={CELL_SELECT} aria-label={`Prevalence for ${e.displayName}`} disabled={!isDraft || busy} value={row.prevalence ?? ""} onChange={(x) => change(e.antigenId, { prevalence: (x.target.value || null) as EntryInput["prevalence"] })}>
                        {[...GRADE_NONE, ...PREVALENCE_GRADES.map((g) => ({ value: g, label: g }))].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Td>
                    <Td>
                      <select className={CELL_SELECT} aria-label={`Severity for ${e.displayName}`} disabled={!isDraft || busy} value={row.severity ?? ""} onChange={(x) => change(e.antigenId, { severity: (x.target.value || null) as EntryInput["severity"] })}>
                        {[...GRADE_NONE, ...SEVERITY_GRADES.map((g) => ({ value: g, label: g }))].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-3">
                        {SEASONS.map((s) => (
                          <label key={s} className="flex items-center gap-1 text-small capitalize">
                            <input
                              type="checkbox"
                              aria-label={`${s} for ${e.displayName}`}
                              disabled={!isDraft || busy}
                              checked={row.seasons.includes(s)}
                              onChange={(x) => change(e.antigenId, { seasons: SEASONS.filter((y) => (y === s ? x.target.checked : row.seasons.includes(y))) })}
                            />
                            {s}
                          </label>
                        ))}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {list.length === 0 && <p className="mt-3 text-ink-secondary">{isDraft ? "No antigens yet. Add the antigens this profile covers." : "This version has no antigens."}</p>}
        </Card>
      )}
    </>
  );
}
